import { Canvas, ObjectId, View } from './view';
import { Matrix } from './matrix';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import { newSelectorChain } from '@fullstory/selectors';

class CanvasSaveStack {
  matrix: number[];
  c2d: CanvasRenderingContext2D;
  bag: any;
  fn: (CanvasSaveStack) => void;
}

export class CanvasState {
  saveStack: CanvasSaveStack[] = [];
  drawChild: (index: ObjectId, matrix: number[]) => void;
  view: View;
  width: number;
  height: number;
  textMatrix: number[];
  textLineMatrix: number[];
  parent: CanvasState | null;

  constructor(
    width: number,
    height: number,
    view: View,
    c2d: CanvasRenderingContext2D,
    parent: CanvasState | null,
    drawChildFn: (index: ObjectId, matrix: number[]) => void,
  ) {
    this.width = width;
    this.height = height;
    this.view = view;
    this.drawChild = drawChildFn;
    this.parent = parent;

    // Pre-populate with position 1 -- this will always be there.
    this.saveStack.unshift({ matrix: Matrix.identity(), c2d, bag: {}, fn: null });
    if (parent) {
      this.saveStack[0].bag['vibrancy'] = parent.fromSaveStackBag('vibrancy');
    }

    this.textMatrix = Matrix.identity();
    this.textLineMatrix = Matrix.identity();
  }

  public finish() {
    while (this.saveStack.length > 1) {
      this.pop();
    }
  }

  public pop() {
    if (this.saveStack.length == 1) {
      console.log('Cowardly refusing to pop last element of save stack');
      return;
    }

    const entry = this.saveStack.shift();
    if (entry.fn) entry.fn(entry);
  }

  public fromSaveStackBag(name: string) {
    for (const i in this.saveStack) {
      if (name in this.saveStack[i].bag) {
        return this.saveStack[i].bag[name];
      }
    }
    return null;
  }

  public selectorChain(): SelectorChain {
    if (this.parent)
      return newSelectorChain(this.parent.selectorChain(), ' ', this.view.getSelectorRule());
    return newSelectorChain(null, '', this.view.getSelectorRule());
  }
}

export abstract class CanvasRendererFactory<OP> {
  // we want inspect mode to only surface views that are actually visible
  abstract visibleForInspectMode(op: Canvas<OP>): boolean;
  abstract drawsChild(op: Canvas<OP>): ObjectId;
  abstract render(state: CanvasState, op: Canvas<OP>): void;

  protected reverseLookup(obj: any, value: number) {
    for (const x in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, x) && obj[x] == value) {
        return x;
      }
    }

    return `Unknown (${value})`;
  }
}
